import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import ko from 'date-fns/locale/ko';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// API
import { getAiChatLogs, getAiChatStatistics, getExchangeRate, GetAiChatLogsInput } from './api/ai.log.api';
import { getFormatDate } from 'common/common.function';

// UI Components
import Container from '../UI/atoms/Container';
import Grid from '../UI/atoms/Grid';
import Button from '../UI/atoms/Button';
import TextField from '../UI/atoms/TextField';
import Tab from '../UI/atoms/Tab'; // Added Import
import Table from '../UI/atoms/Table'; // Shared Table Component
import Pagination from '../UI/molecules/Pagination';
import Line from '../UI/atoms/Line';
import { BiSearch } from 'react-icons/bi';
import { ButtonTag } from './styles/NotcieBoard.style';

registerLocale('ko', ko);
const MySwal = withReactContent(Swal);

const Pre = styled.pre`
  background: #f4f4f4;
  padding: 10px;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  font-size: 13px;
  max-height: 300px;
  overflow-y: auto;
`;

// Detail Modal Content Component
const LogDetailContent = ({ log, renderCost }: { log: any; renderCost: (cost: number) => string }) => {
  const history = log.chatHistory || [];

  return (
    <div style={{ padding: '10px', textAlign: 'left' }}>
      <Grid bottom={10}>
        <strong>Session ID:</strong> {log.sessionId}
      </Grid>
      <Grid bottom={10}>
        <strong>User:</strong> {log.userId} ({log.name || 'Unknown'})
      </Grid>

      <Grid display="flex" gap={20} bottom={20}>
        <Grid width="50%">
          <strong>Tokens</strong>
          <br />
          Input: {log.inputTokens?.toLocaleString() || 0}
          <br />
          Output: {log.outputTokens?.toLocaleString() || 0}
          <br />
          Total: {log.totalTokens?.toLocaleString() || 0}
        </Grid>
        <Grid width="50%">
          <strong>Cost (Estimated)</strong>
          <br />
          Input: {renderCost(log.inputCost || 0)}
          <br />
          Output: {renderCost(log.outputCost || 0)}
          <br />
          Total: {renderCost(log.totalCost || 0)}
        </Grid>
      </Grid>

      <Line color="#eee" />

      <Grid top={20} bottom={10}>
        <strong>Chat History</strong>
      </Grid>

      <div
        style={{ maxHeight: '400px', overflowY: 'auto', background: '#f9f9f9', padding: '10px', borderRadius: '4px' }}
      >
        {history.map((msg: any, idx: number) => (
          <div key={idx} style={{ marginBottom: '15px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: msg.role === 'user' ? '#0072ce' : '#333' }}>
              {msg.role === 'user' ? 'User' : 'Assistant'}
            </div>
            <Pre style={{ background: msg.role === 'user' ? '#fff' : '#eee', border: '1px solid #ddd' }}>
              {msg.content}
            </Pre>
            {msg.file && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                [File] {msg.file.originalName} ({msg.file.size} bytes)
              </div>
            )}
          </div>
        ))}
        {history.length === 0 && <div style={{ color: '#999' }}>No history available</div>}
      </div>
    </div>
  );
};

export default function AiLogsBoard() {
  const [activeTab, setActiveTab] = useState<'logs' | 'stats'>('logs');

  // Log Search States
  const [logs, setLogs] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Search Filters
  const [searchType, setSearchType] = useState<'sessionId' | 'username'>('sessionId');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const searchKeywordRef = useRef(searchKeyword);
  const searchTypeRef = useRef(searchType);
  searchKeywordRef.current = searchKeyword;
  searchTypeRef.current = searchType;

  // Stats Data
  const [stats, setStats] = useState<any[]>([]);

  // Currency State
  const [showKRW, setShowKRW] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(1450); // Default fallback

  const fetchLogs = useCallback(async () => {
    let sessionId: string | undefined;
    let username: string | undefined;

    const keyword = searchKeywordRef.current;
    const type = searchTypeRef.current;

    if (keyword) {
      if (type === 'sessionId') {
        sessionId = keyword;
      } else {
        username = keyword;
      }
    }

    // Ensure start date is beginning of day, end date is end of day
    const start = startDate ? new Date(startDate) : undefined;
    if (start) start.setHours(0, 0, 0, 0);

    const end = endDate ? new Date(endDate) : undefined;
    if (end) end.setHours(23, 59, 59, 999);

    const params: GetAiChatLogsInput = {
      page,
      limit: 20,
      startDate: start,
      endDate: end,
      sessionId,
      username,
    };
    const res = await getAiChatLogs(params);
    if (res.success && res.logs) {
      setLogs(res.logs);
      setTotalPages(res.totalPages || 1);
    }
  }, [page, startDate, endDate]);

  const fetchStats = useCallback(async () => {
    // For Statistics tab, user requested no date picker.
    // We assume "Total" means all time (or reasonable past range).
    // Using 2024-01-01 as start for now to cover all data.
    const statsStartDate = '2024-01-01';
    const statsEndDate = getFormatDate(new Date());

    const res = await getAiChatStatistics({
      startDate: statsStartDate,
      endDate: statsEndDate,
    });
    if (res.success && res.dailyStats) {
      setStats(res.dailyStats);
    }
  }, []);

  const handleSearch = () => {
    setPage(1);
    if (activeTab === 'logs') fetchLogs();
    else fetchStats();
  };

  const handleToggleCurrency = async () => {
    // If switching TO KRW, fetch latest rate
    if (!showKRW) {
      const res = await getExchangeRate();
      if (res.success && res.rate) {
        setExchangeRate(res.rate);
      }
    }
    setShowKRW(!showKRW);
  };

  const renderCost = (costUser: number) => {
    if (!costUser) return showKRW ? '₩0' : '$0.000000';
    if (showKRW) {
      const krw = Math.round(costUser * exchangeRate);
      return `₩${krw.toLocaleString()} (Rate: ${exchangeRate.toLocaleString()})`;
    }
    return `$${costUser.toFixed(6)}`;
  };

  // Simplified renderCost for Tables (No Rate info)
  const renderCostSimple = (costUser: number) => {
    if (!costUser) return showKRW ? '₩0' : '$0.000000';
    if (showKRW) {
      const krw = Math.round(costUser * exchangeRate);
      return `₩${krw.toLocaleString()}`;
    }
    return `$${costUser.toFixed(6)}`;
  };

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
    // else fetchStats(); // fetchStats is called when tab changes to 'stats'
    if (activeTab === 'stats') fetchStats();
  }, [activeTab, page, fetchLogs, fetchStats]);

  // Open Detail Modal
  const openDetailModal = (log: any) => {
    MySwal.fire({
      title: 'AI Log Detail',
      html: <LogDetailContent log={log} renderCost={renderCost} />,
      showConfirmButton: true,
      confirmButtonText: '확인',
      showCloseButton: true, // Native X button
      width: '800px',
    });
  };

  // Calculate Statistics
  const totalStats = stats.reduce(
    (acc, curr) => ({
      totalCalls: acc.totalCalls + Number(curr.totalCalls),

      inputTokens: acc.inputTokens + Number(curr.inputTokens || 0),
      outputTokens: acc.outputTokens + Number(curr.outputTokens || 0),
      totalTokens: acc.totalTokens + Number(curr.totalTokens),

      inputCost: acc.inputCost + Number(curr.inputCost || 0),
      outputCost: acc.outputCost + Number(curr.outputCost || 0),
      totalCost: acc.totalCost + Number(curr.totalCost),
    }),
    { totalCalls: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0, inputCost: 0, outputCost: 0, totalCost: 0 },
  );

  const monthlyStats = stats.reduce((acc: any, curr: any) => {
    const month = curr.date.substring(0, 7); // YYYY-MM
    if (!acc[month]) {
      acc[month] = {
        date: month,
        totalCalls: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        inputCost: 0,
        outputCost: 0,
        totalCost: 0,
      };
    }
    acc[month].totalCalls += Number(curr.totalCalls);
    acc[month].inputTokens += Number(curr.inputTokens || 0);
    acc[month].outputTokens += Number(curr.outputTokens || 0);
    acc[month].totalTokens += Number(curr.totalTokens);

    acc[month].inputCost += Number(curr.inputCost || 0);
    acc[month].outputCost += Number(curr.outputCost || 0);
    acc[month].totalCost += Number(curr.totalCost);
    return acc;
  }, {});

  const monthlyStatsArray = Object.values(monthlyStats).sort((a: any, b: any) => b.date.localeCompare(a.date));

  // Access user info ID (name) helper

  return (
    <Container>
      {/* Sub Tabs */}
      <Grid bottom={20}>
        <Tab
          tabNames={['로그 조회', '통계']}
          tabSwitchValues={['logs', 'stats']}
          size="small"
          tabNodes={[<></>, <></>]}
          tabSwitchEvent={(val: 'logs' | 'stats') => setActiveTab(val)}
          currentTab={activeTab === 'logs' ? '로그 조회' : '통계'}
        />
      </Grid>

      {/* Search Bar */}
      {activeTab === 'logs' && (
        <Grid
          bottom={20}
          display="flex"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}
        >
          {/* Left: Date Picker */}
          <Grid display="inline-flex" alignItems="center" gap={5}>
            <DatePicker
              locale="ko"
              selected={startDate}
              popperPlacement="top-start"
              onChange={(targetDate: Date) => {
                setStartDate(targetDate);
              }}
              dateFormat="yyyy-MM-dd"
              customInput={<TextField variant="outlined" width={120} />}
            />
            <span style={{ margin: '0 5px' }}>~</span>
            <DatePicker
              locale="ko"
              selected={endDate}
              onChange={(date: Date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              popperPlacement="top-start"
              customInput={<TextField variant="outlined" width={120} autoComplete="off" />}
            />
          </Grid>
          {/* Right: Search Type Select + Input & Button */}
          <Grid display="inline-flex" alignItems="center" gap={5}>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as 'sessionId' | 'username')}
              style={{
                padding: '8px 10px',
                borderRadius: '5px',
                border: '1px solid #ccc',
                background: '#fff',
                fontSize: '13px',
                height: '36px',
              }}
            >
              <option value="sessionId">세션 ID</option>
              <option value="username">사용자명</option>
            </select>
            <TextField
              variant="outlined"
              width={250}
              placeholder={searchType === 'sessionId' ? '세션 ID 검색' : '사용자명 검색'}
              value={searchKeyword}
              onChange={(e: any) => setSearchKeyword(e.target.value)}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') handleSearch();
              }}
              style={{ background: '#fff' }}
            />
            <Button width={80} color="main" size="small" borderRadius={5} onClick={handleSearch}>
              검색
            </Button>
            <Button
              width={100}
              color={showKRW ? 'main' : 'gray'}
              size="small"
              borderRadius={5}
              onClick={handleToggleCurrency}
            >
              {showKRW ? 'KRW 보기' : 'USD 보기'}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Content Area */}
      {activeTab === 'logs' ? (
        <Grid>
          <Table
            headerItems={[
              { width: 33, flex: 0.2, headerName: 'ID', align: 'center', headerAlign: 'center', sortable: false },
              { width: 390, flex: 2, headerName: '세션ID', align: 'center', headerAlign: 'center', sortable: false }, // Widen Session ID
              { width: 80, flex: 0.5, headerName: '사용자', align: 'center', headerAlign: 'center', sortable: false },
              {
                width: 150,
                flex: 1,
                headerName: '마지막 질문',
                align: 'left',
                headerAlign: 'center',
                sortable: false,
              },
              // Total Tokens Only
              {
                width: 70,
                flex: 0.6,
                headerName: '합계 토큰',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
              },
              // Total Cost Only
              {
                width: 80,
                flex: 0.6,
                headerName: '합계 비용',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
              },

              {
                width: 150,
                flex: 0.8,
                headerName: '작업 일시',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
              },
              {
                width: 60,
                flex: 0.4,
                headerName: '상세보기',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => (
                  <Grid display="flex" justifyContent="center">
                    <ButtonTag
                      color="white"
                      size="small"
                      variant="outlined"
                      width={40}
                      borderRadius={5}
                      onClick={() => {
                        openDetailModal(params.row.col7);
                      }}
                    >
                      <BiSearch size={16} />
                    </ButtonTag>
                  </Grid>
                ),
              },
            ]}
            bodyItems={logs.map((log) => {
              // Extract prompt
              let promptText = '-';
              if (log.chatHistory && Array.isArray(log.chatHistory)) {
                const userMsgs = log.chatHistory.filter((m: any) => m.role === 'user');
                if (userMsgs.length > 0) {
                  promptText = userMsgs[userMsgs.length - 1].content;
                }
              }

              return [
                log.id,
                log.sessionId, // Full Session ID
                log.name || log.userId || 'Guest', // Show Name (ID) first
                promptText.length > 50 ? promptText.substring(0, 50) + '...' : promptText,

                log.totalTokens,
                renderCostSimple(log.totalCost || 0),

                new Date(log.updatedAt || log.createdAt).toLocaleString(),
                log, // col7 for View button (index starts at 0, so 0-6 are data, 7 is object)
              ];
            })}
          />
          <Grid top={20} display="flex" justifyContent="center">
            <Pagination nowPage={page} max={totalPages} size={5} nowPageEvent={setPage} />
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <Grid bottom={10} display="flex" justifyContent="space-between">
            <h3 style={{ margin: '0 0 10px 0' }}>전체 통계</h3>
            <Button
              width={100}
              color={showKRW ? 'main' : 'gray'}
              size="small"
              borderRadius={5}
              onClick={handleToggleCurrency}
            >
              {showKRW ? 'KRW 보기' : 'USD 보기'}
            </Button>
          </Grid>
          <Table
            headerItems={[
              { width: 150, flex: 1, headerName: '기간', align: 'center', headerAlign: 'center', sortable: false },
              {
                width: 120,
                flex: 1,
                headerName: '총 호출 수',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
              },
              { width: 100, flex: 1, headerName: '입력 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '출력 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '합계 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '입력 비용', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '출력 비용', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '합계 비용', align: 'center', headerAlign: 'center', sortable: false },
            ]}
            bodyItems={[
              [
                '전체',
                totalStats.totalCalls.toLocaleString(),

                totalStats.inputTokens.toLocaleString(),
                totalStats.outputTokens.toLocaleString(),
                totalStats.totalTokens.toLocaleString(),

                renderCostSimple(totalStats.inputCost || 0),
                renderCostSimple(totalStats.outputCost || 0),
                renderCostSimple(totalStats.totalCost || 0),
              ],
            ]}
          />

          <Grid top={30} bottom={10}>
            <h3 style={{ margin: '0 0 10px 0' }}>월간 통계</h3>
          </Grid>
          <Table
            headerItems={[
              { width: 150, flex: 1, headerName: '월', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '호출 수', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '입력 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '출력 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '합계 토큰', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '입력 비용', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '출력 비용', align: 'center', headerAlign: 'center', sortable: false },
              { width: 120, flex: 1, headerName: '합계 비용', align: 'center', headerAlign: 'center', sortable: false },
            ]}
            bodyItems={monthlyStatsArray.map((stat: any) => [
              stat.date,
              stat.totalCalls.toLocaleString(),
              stat.inputTokens.toLocaleString(),
              stat.outputTokens.toLocaleString(),
              stat.totalTokens.toLocaleString(),
              renderCostSimple(stat.inputCost || 0),
              renderCostSimple(stat.outputCost || 0),
              renderCostSimple(stat.totalCost || 0),
            ])}
          />

          <Grid top={30} bottom={10}>
            <h3 style={{ margin: '0 0 10px 0' }}>일별 통계</h3>
          </Grid>
          <Table
            headerItems={[
              { width: 150, flex: 1, headerName: '날짜', align: 'center', headerAlign: 'center', sortable: true },
              { width: 120, flex: 1, headerName: '호출 수', align: 'center', headerAlign: 'center', sortable: true },
              { width: 100, flex: 1, headerName: '입력 토큰', align: 'center', headerAlign: 'center', sortable: true },
              { width: 100, flex: 1, headerName: '출력 토큰', align: 'center', headerAlign: 'center', sortable: true },
              { width: 100, flex: 1, headerName: '합계 토큰', align: 'center', headerAlign: 'center', sortable: true },
              { width: 120, flex: 1, headerName: '입력 비용', align: 'center', headerAlign: 'center', sortable: true },
              { width: 120, flex: 1, headerName: '출력 비용', align: 'center', headerAlign: 'center', sortable: true },
              { width: 120, flex: 1, headerName: '합계 비용', align: 'center', headerAlign: 'center', sortable: true },
            ]}
            bodyItems={stats.map((stat) => [
              stat.date,
              stat.totalCalls,
              stat.inputTokens?.toLocaleString() || '0',
              stat.outputTokens?.toLocaleString() || '0',
              stat.totalTokens.toLocaleString(),
              renderCostSimple(stat.inputCost || 0),
              renderCostSimple(stat.outputCost || 0),
              renderCostSimple(stat.totalCost || 0),
            ])}
          />
        </Grid>
      )}
    </Container>
  );
}
